import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getERPgetStoreAnnualStatistics } from '@/service/erpAPI';

const defaultState = {
  fetching: false,
  error: null,
  query: {},
  data: {},
  // 按 serviceType 存储数据（主槽 = 最近一次「主请求」结果，供卡片等使用）
  all: {},
  package: {},
  postpartum: {},
  /** 按自然年留存年度统计，避免并发/跨年请求时 pending 清空把上一年的冲掉 */
  allByYear: {},
  packageByYear: {},
  postpartumByYear: {},
  // 跟踪正在进行的请求数量（用于并发请求管理）
  _pendingRequests: 0
};

const serviceTypeToByYearField = {
  all: 'allByYear',
  package: 'packageByYear',
  postpartum: 'postpartumByYear'
};
// all: 'all', package: '销售业绩', postpartum: '产后复原'
// 数据记录区分
const switchPayload = (serviceType, query) => {
  const payload = {
    all: () => getERPgetStoreAnnualStatistics(query),
    package: () => getERPgetStoreAnnualStatistics(query),
    postpartum: () => getERPgetStoreAnnualStatistics(query),
    undefined: () => {
      console.error('Redux: fetchERPgetStoreAnnualStatistics 请求未定义 serviceType 类型');
      return Promise.reject(new Error('serviceType 参数无效'));
    }
  };
  return payload[serviceType] || payload.undefined;
};


export const fetchERPgetStoreAnnualStatistics = createAsyncThunk(
  'erpgetStoreAnnualStatistics/fetchERPgetStoreAnnualStatistics',
  async ({ serviceType, query = {} }, { rejectWithValue }) => {
    try {
      if (!serviceType) {
        return rejectWithValue('serviceType 参数是必需的');
      }
      const { packageMainSlot, writeMainSlot, ...queryForApi } = query;
      const response = await switchPayload(serviceType, queryForApi)();
      return {
        data: response.data,
        serviceType,
        query: queryForApi,
        writeMainSlot: writeMainSlot ?? packageMainSlot
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取ERP门店年度统计数据失败');
    }
  }
);

const erpgetStoreAnnualStatisticsSlice = createSlice({
  name: 'erpgetStoreAnnualStatistics',
  initialState: defaultState,
  reducers: {
    resetERPgetStoreAnnualStatisticsState: (state, action) => {
      const serviceType = action.payload?.serviceType;
      if (serviceType && state[serviceType]) {
        // 重置指定服务类型的数据
        state[serviceType] = {};
        const byField = serviceTypeToByYearField[serviceType];
        if (byField) {
          state[byField] = {};
        }
      } else {
        // 重置所有状态
        state.query = {};
        state.data = {};
        state.error = null;
        state.fetching = false;
        state._pendingRequests = 0;
        // 重置所有服务类型的数据
        Object.keys(defaultState).forEach((key) => {
          if (
            key !== 'fetching' &&
            key !== 'error' &&
            key !== 'query' &&
            key !== 'data' &&
            key !== '_pendingRequests'
          ) {
            state[key] = typeof defaultState[key] === 'object' && defaultState[key] !== null && !Array.isArray(defaultState[key])
              ? {}
              : defaultState[key];
          }
        });
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchERPgetStoreAnnualStatistics.pending, (state, action) => {
        // 增加正在进行的请求计数
        state._pendingRequests = (state._pendingRequests || 0) + 1;
        state.fetching = true;
        state.error = null;
        // 不清空 serviceType 主槽与 *ByYear：多请求并发（如 package 不同年份）时避免把已落库的年度冲掉
      })
      .addCase(fetchERPgetStoreAnnualStatistics.fulfilled, (state, action) => {
        // 减少正在进行的请求计数
        state._pendingRequests = Math.max(0, (state._pendingRequests || 0) - 1);
        // 只有当所有请求都完成时，才将 fetching 设置为 false
        state.fetching = state._pendingRequests > 0;
        state.error = null;
        const { data: responseData, serviceType, query, writeMainSlot } = action.payload;
        
        // 处理响应数据
        const { data: dataObj, errcode, errinfo } = responseData || {};
        
        // 注意：不更新通用 data 和 query，因为并发请求时会被覆盖
        // 只按 serviceType 存储数据，避免数据错乱
        // state.data = dataObj || {};
        // state.query = query || {};
        
        // 如果有错误信息，保存到 error（但注意：并发请求时，error 也会被覆盖）
        if (errinfo) {
          state.error = errinfo;
        }

        const qt = query || {};
        const yearKey =
          qt.year != null && String(qt.year).trim() !== '' ? String(qt.year) : null;
        const byField = serviceTypeToByYearField[serviceType];

        if (byField && yearKey) {
          if (!state[byField]) {
            state[byField] = {};
          }
          state[byField][yearKey] = {
            ...(dataObj || {}),
            query: qt
          };
        }

        // 主槽：card/列表仍用「当前筛选年」那一次 package；仅补 byYear 的请求不写主槽（writeMainSlot: false）
        if (writeMainSlot !== false && serviceType && state[serviceType] !== undefined) {
          state[serviceType] = {
            ...(dataObj || {}),
            query: qt
          };
        }
      })
      .addCase(fetchERPgetStoreAnnualStatistics.rejected, (state, action) => {
        // 减少正在进行的请求计数
        state._pendingRequests = Math.max(0, (state._pendingRequests || 0) - 1);
        // 只有当所有请求都完成时，才将 fetching 设置为 false
        state.fetching = state._pendingRequests > 0;
        state.error = action.payload;
        // 失败不清主槽与 byYear，避免某一年失败抹掉已成功的年度数据
      });
  }
});

export const { resetERPgetStoreAnnualStatisticsState } = erpgetStoreAnnualStatisticsSlice.actions;
export default erpgetStoreAnnualStatisticsSlice.reducer;
